import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAccountingStartMutation,
    useOrganizationQuery,
} from '@/api/queries/organizations';
import type { ErrorResponse } from '@/api/handlers/responses';
import { PATH } from '@/router/PATH';

/** The years accounting start accepts; outside them the server says `YEAR_INVALID`. */
export const ACCOUNTING_YEAR_MIN = 2000;
export const ACCOUNTING_YEAR_MAX = 2100;

/**
 * Why the year field is invalid: `range` (empty or outside 2000–2100, also
 * the server's `YEAR_INVALID`) or `closed` (`FISCAL_YEAR_CLOSED`: every
 * period of that year is closed or locked).
 */
export type AccountingYearError = 'range' | 'closed';

export type UseAccountingStartStepOptions = {
    /**
     * The year in the field, `null` while it is empty: the wizard's page
     * holds it, so it outlives the step (Back to Welcome and here again).
     */
    year: number | null;
    /** Writes the year to the page's memory. */
    onYearChange: (year: number | null) => void;
};

export type UseAccountingStartStepResult = {
    /** The organisation does not need this step: the container redirects to Branch. */
    isNotNeeded: boolean;
    /** `null` while the field is empty. */
    year: number | null;
    onYearChange: (year: number | null) => void;
    yearError: AccountingYearError | null;
    /** The year the closed-year refusal was about, for its sentence. */
    closedYear: number | null;
    /** Any other refusal, shown as the step's notice with Retry. */
    failure: ErrorResponse | null;
    isSubmitting: boolean;
    /** Continue, and Retry from the notice: starts accounting for `year`. */
    onContinue: () => void;
    /**
     * Back to Welcome (a new history entry). Nothing is reset: the year
     * stays in the page's memory. The View disables it while the start runs.
     */
    onBack: () => void;
};

const isInRange = (year: number | null): year is number =>
    year !== null &&
    Number.isInteger(year) &&
    year >= ACCOUNTING_YEAR_MIN &&
    year <= ACCOUNTING_YEAR_MAX;

/**
 * Accounting start: one year, the current one until the owner changes it.
 * The year is the wizard page's (`year`, `onYearChange`), so it survives
 * Back to Welcome; the range error and the last refusal are this step's and
 * clear when the year changes. Continue creates that fiscal year and its tax
 * rates; the mutation settles after the organisation is re-read (now
 * `accountingStartNeeded: false`), then the Branch step opens. A closed year
 * is the field's error; anything else is the step's notice. Back opens
 * Welcome by path and resets nothing.
 */
export const useAccountingStartStep = ({
    year,
    onYearChange: writeYear,
}: UseAccountingStartStepOptions): UseAccountingStartStepResult => {
    const navigate = useNavigate();
    const organisation = useOrganizationQuery().data;
    const startMutation = useAccountingStartMutation();
    const [showRangeError, setShowRangeError] = React.useState(false);

    const { error, reset } = startMutation;
    const onYearChange = React.useCallback(
        (next: number | null) => {
            writeYear(next);
            setShowRangeError(false);
            // A refusal was about the previous year.
            reset();
        },
        [reset, writeYear],
    );

    const { mutate } = startMutation;
    const onContinue = React.useCallback(() => {
        if (!isInRange(year)) {
            setShowRangeError(true);
            return;
        }
        mutate(
            { year },
            {
                onSuccess: () =>
                    navigate(PATH.ONBOARDING_STEP.BIND('branch'), {
                        replace: true,
                    }),
            },
        );
    }, [mutate, navigate, year]);

    const onBack = React.useCallback(
        () => navigate(PATH.ONBOARDING_STEP.BIND('start')),
        [navigate],
    );

    const isClosed = error?.code === 'FISCAL_YEAR_CLOSED';
    const isRange = showRangeError || error?.code === 'YEAR_INVALID';

    return {
        isNotNeeded: organisation?.accountingStartNeeded === false,
        year,
        onYearChange,
        yearError: isClosed ? 'closed' : isRange ? 'range' : null,
        closedYear: isClosed ? (startMutation.variables?.year ?? null) : null,
        failure: error && !isClosed && !isRange ? error : null,
        isSubmitting: startMutation.isPending,
        onContinue,
        onBack,
    };
};
